import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Compass, Scroll, Hammer, Leaf, Flower2, Sparkles, ChevronRight, Star, Lock } from 'lucide-react';
import { journeyAPI } from '../api';
import { toast } from 'sonner';

// Journey portal configurations with visual themes
const JOURNEY_PORTALS = {
  'explorer-journey': {
    name: 'Explorer Journey',
    description: 'Begin your adventure through the Temple! Learn foundational skills across different realms.',
    icon: Compass,
    color: '#3B82F6',
    glowColor: 'rgba(59, 130, 246, 0.5)',
    bgGradient: 'from-blue-900/40 to-indigo-900/40',
    borderColor: 'border-blue-500/50',
    theme: 'compass',
    tagline: 'Chart your course through knowledge'
  },
  'ancient-worlds-journey': {
    name: 'Ancient Worlds Journey',
    description: 'Travel back in time to explore ancient civilizations and their wisdom.',
    icon: Scroll,
    color: '#F59E0B',
    glowColor: 'rgba(245, 158, 11, 0.5)',
    bgGradient: 'from-amber-900/40 to-orange-900/40',
    borderColor: 'border-amber-500/50',
    theme: 'temple',
    tagline: 'Uncover secrets of the past'
  },
  'builder-journey': {
    name: 'Builder Journey',
    description: 'Create, design, and build! Learn the skills of creation and imagination.',
    icon: Hammer,
    color: '#10B981',
    glowColor: 'rgba(16, 185, 129, 0.5)',
    bgGradient: 'from-emerald-900/40 to-teal-900/40',
    borderColor: 'border-emerald-500/50',
    theme: 'tools',
    tagline: 'Craft your dreams into reality'
  },
  'nature-journey': {
    name: 'Nature Journey',
    description: 'Discover the wonders of the natural world and our universe.',
    icon: Leaf,
    color: '#22C55E',
    glowColor: 'rgba(34, 197, 94, 0.5)',
    bgGradient: 'from-green-900/40 to-lime-900/40',
    borderColor: 'border-green-500/50',
    theme: 'forest',
    tagline: 'Explore the living world'
  },
  'mindful-journey': {
    name: 'Mindful Journey',
    description: 'Learn to be calm, focused, and kind. Develop inner strength and wisdom.',
    icon: Flower2,
    color: '#8B5CF6',
    glowColor: 'rgba(139, 92, 246, 0.5)',
    bgGradient: 'from-purple-900/40 to-violet-900/40',
    borderColor: 'border-purple-500/50',
    theme: 'lotus',
    tagline: 'Find peace within'
  }
};

const JourneySelection = ({ onBack, selectedChild }) => {
  const navigate = useNavigate();
  const [journeyProgress, setJourneyProgress] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedJourney, setSelectedJourney] = useState(null);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    loadJourneyProgress();
  }, [selectedChild]);

  const loadJourneyProgress = async () => {
    if (!selectedChild?.id) {
      setLoading(false);
      return;
    }

    try {
      const response = await journeyAPI.getChildProgress(selectedChild.id);
      setJourneyProgress(response.data);
    } catch (error) {
      console.error('Failed to load journey progress:', error);
      toast.error('Failed to load journey progress');
    } finally {
      setLoading(false);
    }
  };

  const handleJourneySelect = (journeyId) => {
    setSelectedJourney(journeyId);
    setShowDetails(true);
  };

  const handleStartJourney = (journeyId) => {
    const progress = journeyProgress.find(p => p.journey_id === journeyId);
    if (progress?.next_lesson_id) {
      navigate(`/lesson-player/${progress.next_lesson_id}`);
    } else {
      toast.info('No lessons available in this journey yet');
    }
  };

  const getProgressForJourney = (journeyId) => {
    return journeyProgress.find(p => p.journey_id === journeyId) || {
      completed_lessons: 0,
      total_lessons: 0,
      progress_percent: 0,
      next_lesson_id: null,
      next_lesson_title: null
    };
  };

  // Render portal with glow effect
  const renderPortal = (journeyId, index) => {
    const portal = JOURNEY_PORTALS[journeyId];
    const progress = getProgressForJourney(journeyId);
    const Icon = portal.icon;
    const isSelected = selectedJourney === journeyId;
    const hasProgress = progress.completed_lessons > 0;
    const isComplete = progress.completed_lessons >= progress.total_lessons && progress.total_lessons > 0;

    return (
      <button
        key={journeyId}
        onClick={() => handleJourneySelect(journeyId)}
        className={`relative group transition-all duration-500 transform hover:scale-105 ${
          isSelected ? 'scale-105' : ''
        }`}
        style={{
          animationDelay: `${index * 100}ms`
        }}
        data-testid={`journey-portal-${journeyId}`}
      >
        {/* Portal glow effect */}
        <div 
          className="absolute inset-0 rounded-3xl blur-xl opacity-0 group-hover:opacity-70 transition-opacity duration-500"
          style={{ backgroundColor: portal.glowColor }}
        />
        
        {/* Portal card */}
        <div className={`relative bg-gradient-to-br ${portal.bgGradient} border ${portal.borderColor} rounded-3xl p-6 backdrop-blur-sm overflow-hidden`}>
          {/* Animated background particles */}
          <div className="absolute inset-0 overflow-hidden">
            {[...Array(5)].map((_, i) => (
              <div
                key={i}
                className="absolute w-2 h-2 rounded-full animate-float opacity-30"
                style={{
                  backgroundColor: portal.color,
                  left: `${20 + i * 15}%`,
                  top: `${10 + i * 20}%`,
                  animationDelay: `${i * 0.5}s`,
                  animationDuration: `${3 + i}s`
                }}
              />
            ))}
          </div>
          
          {/* Portal icon */}
          <div className="relative">
            <div 
              className="w-20 h-20 mx-auto rounded-full flex items-center justify-center mb-4 transition-all duration-300 group-hover:scale-110"
              style={{ 
                backgroundColor: `${portal.color}20`,
                boxShadow: `0 0 30px ${portal.glowColor}, inset 0 0 20px ${portal.glowColor}`
              }}
            >
              <Icon 
                className="w-10 h-10 transition-transform duration-300 group-hover:rotate-12" 
                style={{ color: portal.color }}
              />
            </div>
            
            {/* Progress badge */}
            {hasProgress && (
              <div 
                className="absolute -top-2 -right-2 px-2 py-1 rounded-full text-xs font-bold text-white"
                style={{ backgroundColor: portal.color }}
              >
                {isComplete ? (
                  <span className="flex items-center gap-1">
                    <Star className="w-3 h-3 fill-current" /> Complete
                  </span>
                ) : (
                  `${progress.completed_lessons}/${progress.total_lessons}`
                )}
              </div>
            )}
          </div>
          
          {/* Journey name */}
          <h3 className="text-lg font-bold text-white text-center mb-1">{portal.name}</h3>
          <p className="text-sm text-center mb-3 opacity-70" style={{ color: portal.color }}>
            {portal.tagline}
          </p>
          
          {/* Progress bar */}
          <div className="h-1.5 bg-slate-800/50 rounded-full overflow-hidden mb-3">
            <div 
              className="h-full transition-all duration-1000 rounded-full"
              style={{ 
                width: `${progress.progress_percent}%`,
                backgroundColor: portal.color
              }}
            />
          </div>
          
          {/* Progress text */}
          <p className="text-xs text-slate-400 text-center">
            {progress.total_lessons > 0 
              ? `${progress.completed_lessons} of ${progress.total_lessons} lessons`
              : 'Coming soon'
            }
          </p>
        </div>
      </button>
    );
  };

  // Render journey details panel
  const renderDetailsPanel = () => {
    if (!selectedJourney) return null;
    
    const portal = JOURNEY_PORTALS[selectedJourney];
    const progress = getProgressForJourney(selectedJourney);
    const Icon = portal.icon;

    return (
      <div 
        className={`fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm transition-opacity duration-300 ${
          showDetails ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={() => setShowDetails(false)}
      >
        <div 
          className={`relative max-w-lg w-full mx-4 bg-gradient-to-br ${portal.bgGradient} border ${portal.borderColor} rounded-3xl p-8 transform transition-all duration-500 ${
            showDetails ? 'scale-100 translate-y-0' : 'scale-95 translate-y-4'
          }`}
          onClick={(e) => e.stopPropagation()}
          style={{
            boxShadow: `0 0 60px ${portal.glowColor}`
          }}
        >
          {/* Close button */}
          <button
            onClick={() => setShowDetails(false)}
            className="absolute top-4 right-4 p-2 rounded-full bg-slate-800/50 text-slate-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          
          {/* Portal header */}
          <div className="text-center mb-6">
            <div 
              className="w-24 h-24 mx-auto rounded-full flex items-center justify-center mb-4"
              style={{ 
                backgroundColor: `${portal.color}20`,
                boxShadow: `0 0 40px ${portal.glowColor}`
              }}
            >
              <Icon className="w-12 h-12" style={{ color: portal.color }} />
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">{portal.name}</h2>
            <p className="text-slate-300">{portal.description}</p>
          </div>
          
          {/* Progress section */}
          <div className="bg-slate-900/50 rounded-2xl p-4 mb-6">
            <div className="flex justify-between items-center mb-2">
              <span className="text-slate-400">Your Progress</span>
              <span className="font-bold" style={{ color: portal.color }}>
                {progress.progress_percent.toFixed(0)}%
              </span>
            </div>
            <div className="h-3 bg-slate-800 rounded-full overflow-hidden mb-2">
              <div 
                className="h-full transition-all duration-1000 rounded-full"
                style={{ 
                  width: `${progress.progress_percent}%`,
                  backgroundColor: portal.color
                }}
              />
            </div>
            <p className="text-sm text-slate-400 text-center">
              {progress.completed_lessons} of {progress.total_lessons} lessons completed
            </p>
          </div>
          
          {/* Next lesson preview */}
          {progress.next_lesson_title && (
            <div className="mb-6">
              <p className="text-sm text-slate-400 mb-2">Next Lesson:</p>
              <div 
                className="p-4 rounded-xl border"
                style={{ 
                  backgroundColor: `${portal.color}10`,
                  borderColor: `${portal.color}30`
                }}
              >
                <p className="text-white font-semibold">{progress.next_lesson_title}</p>
              </div>
            </div>
          )}
          
          {/* Action button */}
          <button
            onClick={() => handleStartJourney(selectedJourney)}
            disabled={!progress.next_lesson_id}
            className="w-full py-4 rounded-xl font-bold text-white transition-all duration-300 hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            style={{ 
              backgroundColor: portal.color,
              boxShadow: `0 0 20px ${portal.glowColor}`
            }}
            data-testid="begin-journey-btn"
          >
            {progress.completed_lessons > 0 ? (
              <>
                Continue Journey
                <ChevronRight className="w-5 h-5" />
              </>
            ) : (
              <>
                <Sparkles className="w-5 h-5" />
                Begin Journey
              </>
            )}
          </button>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-purple-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-indigo-950/30 to-slate-950 p-4 sm:p-8">
      {/* Background effects */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl" />
      </div>
      
      <div className="relative max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={onBack}
            className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors mb-6"
            data-testid="journey-back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back to Temple</span>
          </button>
          
          <div className="text-center">
            <h1 className="text-3xl sm:text-4xl font-philosopher font-bold text-white mb-3 glow-text">
              Choose Your Path
            </h1>
            <p className="text-slate-400 max-w-xl mx-auto">
              Each journey is a unique adventure through the Temple. Your progress is saved, 
              so you can explore different paths whenever you wish.
            </p>
          </div>
        </div>
        
        {/* Journey portals grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {Object.keys(JOURNEY_PORTALS).map((journeyId, index) => 
            renderPortal(journeyId, index)
          )}
        </div>
        
        {/* Hint text */}
        <div className="mt-8 text-center">
          <p className="text-slate-500 text-sm">
            Tap a portal to learn more about each journey
          </p>
        </div>
      </div>
      
      {/* Journey details modal */}
      {renderDetailsPanel()}
      
      {/* Floating animation keyframes */}
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0) rotate(0deg); }
          50% { transform: translateY(-20px) rotate(180deg); }
        }
        .animate-float {
          animation: float 4s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

export default JourneySelection;
